<template>
  <section>
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <p class="level-item">
          <b-button type="is-primary" icon-left="plus" @click="onAdd">
            {{ $t('attributes.add') + $t('attributes.plan') }}
          </b-button>
        </p>
      </div>
    </nav>

    <b-table
      :key="code"
      :data="plans"
      :loading="loading"
      :row-class="row => row.main_price && 'has-background-danger-light'"
      :mobile-cards="false"
      narrowed
      bordered
    >
      <b-table-column
        field="sort"
        :width="80"
        :label="$t('attributes.sort')"
        numeric
        v-slot="props"
      >
        <p v-if="!props.row.editing">
          {{ props.row.sort }}
        </p>
        <b-field v-else>
          <b-select v-model="props.row.modify.sort" name="sort">
            <option v-for="option in sort_range" :key="option" :value="option">
              {{ option }}
            </option>
          </b-select>
        </b-field>
      </b-table-column>

      <b-table-column
        field="is_main"
        width="90"
        :label="$t('attributes.main_plan')"
        centered
        v-slot="props"
      >
        <b-radio
          v-model="main_plan_id"
          :native-value="props.row.id"
          style="margin-top: 0.5rem; margin-right: 0;"
          @input="onSelectMainPlan"
          :disabled="!props.row.id"
        >
        </b-radio>
      </b-table-column>

      <b-table-column
        field="name"
        :width="180"
        :label="$t('attributes.name')"
        v-slot="props"
      >
        <p v-if="!props.row.editing">
          {{ props.row.name }}
        </p>
        <b-field v-else>
          <b-input
            v-model="props.row.modify.name"
            :placeholder="$t('attributes.name')"
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        v-for="count in traveler_type_count"
        :key="traveler_types[count - 1].id"
        field="travel_types.price"
        :width="150"
        :label="traveler_types[count - 1].traveler_type.display_name"
        v-slot="props"
      >
        <p v-if="!props.row.editing">
          {{ props.row.traveler_types[count - 1].price }}
        </p>
        <b-field v-else>
          <b-input
            v-model.number="props.row.traveler_types[count - 1].modify.price"
            type="number"
            :placeholder="$t('attributes.price')"
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column :width="180" label="功能" centered v-slot="props">
        <div v-if="!props.row.id" class="buttons is-centered">
          <b-button type="is-warning" @click.prevent="onSave(props.row)">
            {{ $t('attributes.save') }}
          </b-button>
          <b-button
            type="is-success"
            outlined
            @click.prevent="onCancel(props.row)"
          >
            {{ $t('attributes.cancel') }}
          </b-button>
        </div>
        <div v-else-if="props.row.editing" class="buttons is-centered">
          <b-button type="is-primary" @click.prevent="onSave(props.row)">
            {{ $t('attributes.save') }}
          </b-button>
          <b-button
            type="is-success"
            outlined
            @click.prevent="onCancel(props.row)"
          >
            {{ $t('attributes.cancel') }}
          </b-button>
        </div>
        <div v-else class="buttons is-centered">
          <b-button type="is-primary" @click.prevent="onEditing(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button type="is-danger" @click.prevent="onDelete(props.row)">
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading :active="sending" :is-full-page="false"></b-loading>
  </section>
</template>

<script>
import { PRODUCTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'PlanList',

  props: {
    product: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      code: this.product.code,
      sort_range: [...Array(20).keys()].map(i => i + 1),

      loading: false,

      plans: [],
      main_plan_id: null,
      traveler_types: [],
      traveler_type_count: 0,
      adding: false,

      sending: false
    }
  },
  watch: {
    product(newProduct) {
      this.code = newProduct.code
      this.plans = []
      this.main_plan_id = null
      this.adding = false
      this.loadPlansAsync(newProduct.code)
    }
  },
  mounted() {
    this.loadPlansAsync(this.product.code)
  },
  methods: {
    async loadPlansAsync(code) {
      try {
        this.loading = true
        const api = `${PRODUCTS_API}/${code}/plans`
        const res = await this.axios.get(api)
        const result = res.data.data
        this.updatePlans(result)
        this.traveler_types = result ? result[0].traveler_types : []
        this.traveler_type_count = this.traveler_types.length
      } catch (e) {
        this.plans = []
        this.main_plan_id = null

        notifyService.fetch_error('plan', e)
      } finally {
        this.loading = false
      }
    },
    updatePlans(plans) {
      this.plans = plans
        .sort((t1, t2) => t1.sort - t2.sort)
        .map(t => this.extractPlan(t))

      const main_plan = this.plans.find(t => t.is_main)
      this.main_plan_id = main_plan && main_plan.id
    },
    extractPlan(item) {
      return {
        id: item.id,
        is_main: item.is_main,
        sort: item.sort,
        name: item.name,
        traveler_types: item.traveler_types
          .sort((t1, t2) => t1.sort - t2.sort)
          .map(t => ({
            name: t.traveler_type.display_name,
            main_price: t.main_price,
            price: t.price,
            traveler_type_id: t.traveler_type_id,
            modify: {}
          })),
        editing: false,
        modify: {}
      }
    },
    onAdd() {
      if (this.adding) {
        return
      }

      this.adding = true
      const item = {
        editing: true,
        id: null,
        sort: 10,
        name: '',
        traveler_types: this.plans[0].traveler_types
          .sort((t1, t2) => t1.sort - t2.sort)
          .map(t => ({
            name: t.name,
            main_price: t.main_price,
            price: 0,
            traveler_type_id: t.traveler_type_id,
            modify: {
              price: 0
            }
          })),
        modify: {
          sort: 10,
          name: ''
        }
      }

      this.plans.push(item)
    },
    onEditing(item) {
      item.editing = true
      item.modify = {
        sort: item.sort,
        name: item.name
      }
      item.traveler_types = item.traveler_types.map(t => ({
        ...t,
        modify: {
          price: t.price
        }
      }))
    },
    onCancel(item) {
      item.editing = false

      if (item.id == null) {
        this.adding = false
        this.plans = this.plans.filter(t => t.id != null)
      }
    },
    onSelectMainPlan(id) {
      this.setMainPlan(id)
    },
    onSave(item) {
      if (!item.modify.name) {
        const msg = this.$t('validation.required', {
          attribute: this.$t('attributes.name')
        })
        notifyService.error(msg)
        return
      }
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    onDelete(item) {
      if (this.plans.length <= 1) {
        notifyService.error(this.$t('message.can_not_delete_last_plan'))
        return
      }

      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete_with_name', {
          name: item.name
        }),
        focusOn: 'cancel',
        onConfirm: () => {
          this.deleteItem(item)
        }
      })
    },
    async createItem(item) {
      try {
        const api = `${PRODUCTS_API}/${this.code}/plans`
        const data = {
          sort: item.modify.sort,
          name: item.modify.name,
          traveler_types: item.traveler_types.map(t => ({
            price: t.modify.price,
            traveler_type_id: t.traveler_type_id
          }))
        }

        this.sending = true
        const res = await this.axios.post(api, data)
        const plan = res.data.data

        const plans = [...this.plans, this.extractPlan(plan)]

        this.plans = plans
          .filter(t => t.id !== null)
          .sort((t1, t2) => t1.sort - t2.sort)
        this.adding = false

        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${PRODUCTS_API}/${this.code}/plans/${item.id}`
        const data = {
          sort: item.modify.sort,
          name: item.modify.name,
          traveler_types: item.traveler_types.map(t => ({
            price: t.modify.price,
            traveler_type_id: t.traveler_type_id
          }))
        }

        this.sending = true
        const res = await this.axios.put(api, data)
        const plan = res.data.data

        const idx = this.plans.findIndex(t => t.id === plan.id)
        const plans = [...this.plans]
        plans[idx] = this.extractPlan(plan)
        this.plans = plans.sort((t1, t2) => t1.sort - t2.sort)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async setMainPlan(id) {
      try {
        const api = `${PRODUCTS_API}/${this.code}/plans/main-plan`

        this.sending = true
        const res = await this.axios.put(api, { id })
        const plan = res.data.data
        this.main_plan_id = plan.id
        this.plans = this.plans
          .map(p => ({ ...p, is_main: p.id === plan.id }))
          .sort((t1, t2) => t1.sort - t2.sort)

        notifyService.success(this.$t('attributes.update_main_plan_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteItem(item) {
      try {
        const api = `${PRODUCTS_API}/${this.code}/plans/${item.id}`

        this.sending = true
        await this.axios.delete(api, item)
        this.plans = this.plans.filter(t => t.id !== item.id)

        notifyService.success(this.$t('attributes.delete_succeed'))
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    getPlans() {
      return this.plans
    }
  }
}
</script>

<style lang="scss" scoped>
.table {
  td {
    vertical-align: middle !important;
  }
}
</style>
